import React, { createContext, useCallback, useContext, useState } from 'react'
import type { ChartConfig } from '@/interfaces/waveChart'

// =============================================================================
// Simple Chart Configuration Management
// =============================================================================

interface ChartConfigContextValue {
  chartConfigs: ChartConfig[]
  setChartConfigs: (configs: ChartConfig[]) => void
  addChart: () => void
  removeChart: (chartId: string) => void
}

const ChartConfigContext = createContext<ChartConfigContextValue | null>(null)

export function useChartConfig() {
  const context = useContext(ChartConfigContext)
  if (!context) {
    throw new Error('useChartConfig must be used within a ChartConfigProvider')
  }
  return context
}

interface ChartConfigProviderProps {
  children: React.ReactNode
}

export function ChartConfigProvider({ children }: ChartConfigProviderProps) {
  const [chartConfigs, setChartConfigs] = useState<ChartConfig[]>([{
    id: 'default-chart',
    title: 'Chart 1',
    curveReferences: [],
    yAxes: {}
  }])

  const addChart = useCallback(() => {
    setChartConfigs(prev => {
      const newChart: ChartConfig = {
        id: `chart-${Date.now()}`,
        title: `Chart ${prev.length + 1}`,
        curveReferences: [],
        yAxes: {}
      }
      return [...prev, newChart]
    })
  }, [])

  const removeChart = useCallback((chartId: string) => {
    setChartConfigs(prev => prev.filter(c => c.id !== chartId))
  }, [])

  const contextValue: ChartConfigContextValue = {
    chartConfigs,
    setChartConfigs,
    addChart,
    removeChart
  }

  return (
    <ChartConfigContext.Provider value={contextValue}>
      {children}
    </ChartConfigContext.Provider>
  )
}